import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Form, Spinner } from 'react-bootstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { observer } from 'mobx-react';

import CsLineIcons from 'cs-line-icons/CsLineIcons';

import LayoutStaffPortal from 'layout/LayoutStaffPortal';
import JudgeTabulatorHelpMessage from 'views/judgeportal/JudgeTabulatorHelpMessage';

import { GetStaffPortalStore } from 'SessionState/Staff/StaffPortalStore';

type StaffLoginFormValues = {
  accessCode: string;
};

const StaffLogin = observer(() => {
  const navigate = useHistory();

  const [showHelpModal, setShowHelpModal] =
    useState(false);

  const [loginError, setLoginError] =
    useState<string | null>(null);

  const [formLoading, setFormLoading] =
    useState(false);

  useEffect(() => {
    let manifestLink =
      document.querySelector(
        'link[rel="manifest"]',
      ) as HTMLLinkElement | null;

    if (!manifestLink) {
      manifestLink =
        document.createElement('link');

      manifestLink.rel = 'manifest';

      document.head.appendChild(
        manifestLink,
      );
    }

    manifestLink.href =
      '/staff/manifest.json';

    document.title =
      'HelloTiara Staff Portal';
  }, []);

  const validationSchema =
    Yup.object().shape({
      accessCode: Yup.string()
        .trim()
        .min(
          4,
          'Code is too short',
        )
        .required(
          'staff code is required',
        ),
    });

  const onSubmit = async (
    values: StaffLoginFormValues,
  ): Promise<void> => {
    try {
      setFormLoading(true);

      setLoginError(null);

      const normalizedCode =
        values.accessCode
          .trim()
          .toUpperCase();

      const tabulatorStore =
        GetStaffPortalStore();

      const result =
        await tabulatorStore.loginWithAccessCode(
          normalizedCode,
        );

      if (!result) {
        setLoginError(
          tabulatorStore.error ??
            'Invalid code or access is no longer active.',
        );

        return;
      }

      navigate.push(
        '/staff/dashboard',
      );
    } catch (error) {
      console.error(
        'Error logging into Staff Portal:',
        error,
      );

      setLoginError(
        'Invalid code or access is no longer active.',
      );
    } finally {
      setFormLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      accessCode: '',
    },

    validationSchema,

    onSubmit,
  });

  const {
    handleSubmit,
    handleChange,
    values,
    touched,
    errors,
  } = formik;

  const Loading = ({
    isLoading,
  }: {
    isLoading: boolean;
  }) => {
    if (!isLoading) {
      return null;
    }

    return (
      <Spinner
        as="span"
        animation="border"
        size="sm"
        className="me-2"
      />
    );
  };

  const content = (
    <div
      className="bg-foreground d-flex justify-content-center align-items-center overflow-hidden"
      style={{
        minHeight:
          'calc(90vh - var(--nav-size-slim))',
      }}
    >
      <div
        className="text-center"
        style={{
          width: '100%',
          maxWidth: '500px',
          padding: '2rem',
        }}
      >
        <div className="mb-4">
          <h1 className="display-3 fw-bold text-primary">
            Staff Portal
          </h1>
        </div>

        <div className="mb-5">
          <p className="h6">
            Enter the staff access
            code provided for this
            pageant.
          </p>
        </div>

        <form
          id="staffLoginForm"
          className="tooltip-end-bottom"
          onSubmit={handleSubmit}
        >
          <div className="mb-3 filled form-group tooltip-end-top">
            <CsLineIcons icon="key" />

            <Form.Control
              type="text"
              name="accessCode"
              placeholder="Staff Code"
              value={values.accessCode}
              onChange={handleChange}
              autoComplete="off"
              autoCapitalize="characters"
              maxLength={4}
            />

            {errors.accessCode &&
              touched.accessCode && (
                <div className="d-block invalid-tooltip">
                  {errors.accessCode}
                </div>
              )}
          </div>

          <div className="d-flex justify-content-center">
            <Button
              size="lg"
              type="submit"
              disabled={formLoading}
            >
              <Loading
                isLoading={
                  formLoading
                }
              />

              <span>
                Enter Portal
              </span>
            </Button>
          </div>

          {loginError && (
            <div className="mt-3 text-center text-danger">
              {loginError}
            </div>
          )}
        </form>

        <div className="mt-4">
          <Button
            variant="outline-primary"
            onClick={() =>
              setShowHelpModal(
                true,
              )
            }
          >
            Message for Help
          </Button>
        </div>

        <div className="text-center text-muted w-100 mt-4">
          <a
            aria-current="page"
            href="/"
          >
            <em>
              Leave Staff
              Portal and Go To
              HelloTiara.com
            </em>
          </a>
        </div>
      </div>
    </div>
  );

  return (
    <LayoutStaffPortal
      staffView="login"
    >
      {content}

      <JudgeTabulatorHelpMessage
        show={showHelpModal}
        onHide={() =>
          setShowHelpModal(false)
        }
        mode="login-help"
        initialMessage="I need help with my staff code. I either need my code or my code is not working."
      />
    </LayoutStaffPortal>
  );
});

export default StaffLogin;